import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useBillableItems } from '@/hooks';
import { ViewTabs, ViewTab } from '@/components/ui/view-tabs';
import { FilterBar } from '@/components/ui/filter-bar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ExternalLink, Plus } from 'lucide-react';
import { CategoryBadge } from '@/components/billable-items/CategoryBadge';
import { usePaginationState } from '@/hooks/usePaginationState';
import { PaginationInfo } from '@/components/PaginationInfo';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';
import { calculatePaginationValues } from '@/lib/pagination';

// View tabs configuration
const billableViews: ViewTab[] = [
  { value: 'ready', label: 'Ready to Invoice' },
  { value: 'current', label: 'Current Month' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'all', label: 'All Items' },
];


export default function BillableItemsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filters from URL params
  const currentView = searchParams.get('view') || 'all';
  const search = searchParams.get('search') || '';

  // Phase 6: Pagination state management in URL
  const { page, limit, offset, setPage } = usePaginationState(50);

  // Calculate current month for "Current Month" and "Overdue" views
  const currentDate = new Date();
  const currentMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

  // Determine API filters based on current view
  let apiFilters: any = {
    search: search || undefined,
    limit,
    offset,
  };

  switch (currentView) {
    case 'ready':
      // Ready to Invoice: unbilled items, not cancelled
      apiFilters.unbilled_only = true;
      apiFilters.cancelled = false;
      break;
    case 'current':
      // Current Month: scheduled_month = current month
      apiFilters.scheduled_month_after = currentMonth;
      apiFilters.scheduled_month_before = currentMonth;
      break;
    case 'overdue':
      // Overdue: scheduled_month < current month, unbilled
      apiFilters.scheduled_month_before = currentMonth;
      apiFilters.unbilled_only = true;
      break;
    // 'all': no additional filters
  }

  const { data, isLoading, error } = useBillableItems(apiFilters, {
    keepPreviousData: true, // Smooth page transitions
  });

  const items = data?.billable_items || [];
  const total = data?.total || 0;

  // Calculate pagination values
  const paginationValues = calculatePaginationValues(total, offset, limit);

  // Handle view tab change - reset to page 1
  const handleViewChange = (view: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (view === 'all') {
      newParams.delete('view');
    } else {
      newParams.set('view', view);
    }
    // Phase 6: Reset to page 1 when view changes
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle search change - reset to page 1
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    // Phase 6: Reset to page 1 when search changes (combined to avoid race condition)
    newParams.delete('page');
    setSearchParams(newParams);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    // Clear all params (search, view, page, etc.)
    setSearchParams(new URLSearchParams());
  };

  // Phase 6: Handle page overflow - redirect to last valid page if current page exceeds total
  useEffect(() => {
    if (!isLoading && data && paginationValues.totalPages > 0) {
      if (page > paginationValues.totalPages) {
        setPage(paginationValues.totalPages);
      }
    }
  }, [page, paginationValues.totalPages, isLoading, data, setPage]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'outline';
      case 'confirmed':
        return 'default';
      case 'invoiced':
        return 'secondary';
      case 'paid':
        return 'default';
      case 'cancelled':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const handleRowClick = (item: any) => {
    if (item.project_id) {
      navigate(`/projects/${item.project_id}`);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Billable Items">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Billable Items"
      actions={
        // TODO Phase 5: Implement standalone billable item creation
        // For now, redirect to projects - billable items are created within project context
        // Future: Add project selector → create billable item for selected project
        <Button onClick={() => navigate('/projects')}>
          <Plus className="mr-2 h-4 w-4" />
          New Item
        </Button>
      }
    >
      <div className="mb-4">
        <p className="text-muted-foreground">
          Track billable work and prepare invoices
        </p>
      </div>

      {/* View Tabs */}
      <ViewTabs
        views={billableViews}
        currentView={currentView}
        onChange={handleViewChange}
      />

      {/* Filter Bar */}
      <FilterBar
        search={search}
        onSearchChange={handleSearchChange}
        placeholder="Search description or project..."
        onClearAll={handleClearAll}
        activeFilterCount={search ? 1 : 0}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load billable items: {(error as Error).message}
          </p>
        </div>
      )}

      {/* Table */}
      {!error && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Project</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Invoiced</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Remaining</TableHead>
                <TableHead>Scheduled</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center h-32">
                    <div className="flex flex-col items-center justify-center text-muted-foreground">
                      <p>No billable items found</p>
                      {search && (
                        <p className="text-sm mt-1">
                          Try adjusting your search or filters
                        </p>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: any) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(item)}
                  >
                    <TableCell className="font-medium max-w-md truncate">
                      {item.description}
                    </TableCell>
                    <TableCell>
                      <CategoryBadge category={item.category || 'professional_fee'} />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{item.project_number}</span>
                        {item.project_name && (
                          <span className="text-sm text-muted-foreground truncate max-w-[200px]">
                            {item.project_name}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(item.amount)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(item.invoiced_amount || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(item.paid_amount || 0)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(item.remaining_amount || 0)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {item.scheduled_month ? formatDate(item.scheduled_month) : '-'}
                    </TableCell>
                    <TableCell>
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Phase 6: Pagination Footer */}
      {!error && total > 0 && (
        <div className="flex items-center justify-between">
          <PaginationInfo
            total={total}
            start={paginationValues.start}
            end={paginationValues.end}
            itemName="billable items"
          />

          {paginationValues.totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage(page - 1)}
                    className={!paginationValues.hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>

                {/* First page */}
                {paginationValues.currentPage > 2 && (
                  <PaginationItem>
                    <PaginationLink onClick={() => setPage(1)} className="cursor-pointer">
                      1
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Left ellipsis */}
                {paginationValues.currentPage > 3 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Previous page */}
                {paginationValues.hasPreviousPage && (
                  <PaginationItem>
                    <PaginationLink onClick={() => setPage(page - 1)} className="cursor-pointer">
                      {page - 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Current page */}
                <PaginationItem>
                  <PaginationLink isActive className="cursor-default">
                    {page}
                  </PaginationLink>
                </PaginationItem>

                {/* Next page */}
                {paginationValues.hasNextPage && (
                  <PaginationItem>
                    <PaginationLink onClick={() => setPage(page + 1)} className="cursor-pointer">
                      {page + 1}
                    </PaginationLink>
                  </PaginationItem>
                )}

                {/* Right ellipsis */}
                {paginationValues.currentPage < paginationValues.totalPages - 2 && (
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                )}

                {/* Last page */}
                {paginationValues.currentPage < paginationValues.totalPages - 1 && (
                  <PaginationItem>
                    <PaginationLink
                      onClick={() => setPage(paginationValues.totalPages)}
                      className="cursor-pointer"
                    >
                      {paginationValues.totalPages}
                    </PaginationLink>
                  </PaginationItem>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage(page + 1)}
                    className={!paginationValues.hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
